import { useInterfaceText } from "./InterfaceText";
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Crown, X } from 'lucide-react';
import api from '../lib/api';

/**
 * Global slim banner shown when user is on a 14-day Pro trial.
 * Visible across the entire app. Dismissible per-session via localStorage.
 * Auto-hides on the pricing page (no double messaging).
 */
export default function TrialBanner() {
  const __bcUi = useInterfaceText();

  const [trial, setTrial] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Hide if user dismissed this session
    if (sessionStorage.getItem('trial_banner_dismissed') === '1') {
      setDismissed(true);
    }
    fetchTrial();
    // Refresh trial state every 5 minutes
    const id = setInterval(fetchTrial, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const fetchTrial = async () => {
    try {
      const { data } = await api.get('/subscription');
      if (data.trial_active) {
        setTrial({
          days: data.trial_days_remaining,
          end: data.trial_end,
        });
      } else {
        setTrial(null);
      }
    } catch (_) {
      // silent fail — banner is purely UX, not critical
    }
  };

  // Don't render on pricing page (banner already there)
  if (!trial || dismissed || location.pathname.startsWith('/pricing')) return null;

  const isUrgent = trial.days <= 3;

  return (
    <div
      data-testid="global-trial-banner"
      className={`fixed top-0 left-0 right-0 z-[55] lg:left-64 px-4 py-2 text-sm flex items-center justify-center gap-2 shadow ${
        isUrgent
          ? 'bg-gradient-to-r from-red-600 via-rose-600 to-red-600 text-white'
          : 'bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-slate-900'
      }`}
      style={{ marginTop: '64px' }}
    >
      <Crown className="w-4 h-4 flex-shrink-0" />
      <span className="font-semibold truncate">
        {isUrgent
          ? `¡Solo ${trial.days} ${trial.days === 1 ? 'día' : 'días'} de prueba PRO!`
          : `Prueba PRO activa · ${trial.days} ${trial.days === 1 ? 'día restante' : 'días restantes'}`}
      </span>
      <button
        onClick={() => navigate('/pricing')}
        data-testid="trial-banner-upgrade-btn"
        className={`ml-2 font-bold underline hover:no-underline ${
          isUrgent ? 'text-white' : 'text-slate-900'
        }`}
      >
        Suscribirme
      </button>
      <button
        onClick={() => {
          sessionStorage.setItem('trial_banner_dismissed', '1');
          setDismissed(true);
        }}
        data-testid="trial-banner-dismiss-btn"
        className="ml-auto opacity-70 hover:opacity-100"
        aria-label={__bcUi("Cerrar")}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
